import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../lib/api';
import { authStorage } from './authStorage';

// Types
interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  attributes?: Record<string, any>;
}

interface AuthState {
  loading: boolean;
  userInfo: User | null;
  userToken: string | null;
  accessTokenExpiresAt: number | null;
  refreshTokenExpiresAt: number | null;
  error: string | null;
  success: boolean;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  loading: false,
  userInfo: null,
  userToken: null,
  accessTokenExpiresAt: null,
  refreshTokenExpiresAt: null,
  error: null,
  success: false,
  isAuthenticated: false,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: { identifier: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.login(credentials.identifier, credentials.password);
      
      if (!response.authenticated || !response.user) {
        return rejectWithValue('Ungültige Anmeldedaten');
      }

      const userData: User = {
        id: response.user.subject || 'unknown',
        username: response.user.username || '',
        email: response.user.email || '',
        name: response.user.name || '',
        firstName: response.user.givenName || '',
        lastName: response.user.familyName || '',
        roles: response.user.roles || [],
        attributes: response.user.attributes || {},
      };

      // Store using abstraction
      authStorage.setAuthData(userData);

      return {
        user: userData,
        token: 'session-token',
        accessTokenExpiresAt: response.accessTokenExpiresAt ?? null,
        refreshTokenExpiresAt: response.refreshTokenExpiresAt ?? null,
      };
    } catch (error: any) {
      console.error('🔄 Redux loginUser - error:', error);
      const detail = error?.status === 401 ? 'Ungültige Anmeldedaten' : 'Login fehlgeschlagen';
      return rejectWithValue(detail);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      console.log('🔄 Redux logoutUser thunk called');

      // Clear all auth-related persistent data
      authStorage.clearAuthData();

      // Force reset the auth state immediately
      dispatch(resetAuth());

      console.log('🔄 Redux logoutUser - all data cleared and state reset');
      console.log('🔄 Redux logoutUser - logout successful');
      return true;
    } catch (error) {
      console.error('🔄 Redux logoutUser - error:', error);
      return rejectWithValue('Logout failed');
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      // console.log('🔄 Redux checkAuthStatus thunk called');

      // First, check backend session (most reliable)
      try {
        const response = await fetch('/api/auth/me', {
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.authenticated && data.user) {
            // console.log(
            //   '🔄 Redux checkAuthStatus - backend session found:',
            //   data.user
            // );

            // Update localStorage to match backend session
            const userData = {
              id: data.user.subject || data.user.username || '',
              username: data.user.username || '',
              email: data.user.email || '',
              name:
                data.user.name ||
                `${data.user.givenName || ''} ${data.user.familyName || ''}`.trim(),
              firstName: data.user.givenName || data.user.firstName || '',
              lastName: data.user.familyName || data.user.lastName || '',
              roles: data.user.roles || [],
              attributes: data.user.attributes || {},
            };
            authStorage.setAuthData(userData);

            return {
              user: userData,
              token: 'session-token',
              accessTokenExpiresAt: data.accessTokenExpiresAt ?? null,
              refreshTokenExpiresAt: data.refreshTokenExpiresAt ?? null,
            };
          }
          console.log(
            '🔄 Redux checkAuthStatus - backend session missing or unauthenticated'
          );
          authStorage.clearAuthData();
          return null;
        } else {
          console.warn(
            '🔄 Redux checkAuthStatus - backend returned',
            response.status
          );
          authStorage.clearAuthData();
          return null;
        }
      } catch (backendError) {
        console.log(
          '🔄 Redux checkAuthStatus - backend check failed, falling back to localStorage:',
          backendError
        );
      }

      // Fallback to storage abstraction
      const authData = authStorage.getAuthData();

      if (authData) {
        console.log('🔄 Redux checkAuthStatus - user found in local storage:', authData.user);
        return {
          user: authData.user,
          token: authData.token,
          accessTokenExpiresAt: null,
          refreshTokenExpiresAt: null,
        };
      } else {
        // console.log(
        //   '🔄 Redux checkAuthStatus - no valid auth data found, returning null'
        // );
        return null;
      }
    } catch (error) {
      console.error('🔄 Redux checkAuthStatus - error:', error);
      return rejectWithValue('Auth check failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearSuccess: state => {
      state.success = false;
    },
    resetAuth: state => {
      // console.log('🔄 Redux resetAuth reducer called');
      state.loading = false;
      state.userInfo = null;
      state.userToken = null;
      state.error = null;
      state.success = false;
      state.isAuthenticated = false;
    },
  },
  extraReducers: builder => {
    builder
      // Login cases
      .addCase(loginUser.pending, state => {
        console.log('🔄 Redux loginUser.pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        console.log('🔄 Redux loginUser.fulfilled:', action.payload);
        state.loading = false;
        state.success = true;
        state.isAuthenticated = true;
        state.userInfo = action.payload.user;
        state.userToken = action.payload.token;
        state.accessTokenExpiresAt =
          (action.payload as any).accessTokenExpiresAt ?? null;
        state.refreshTokenExpiresAt =
          (action.payload as any).refreshTokenExpiresAt ?? null;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        console.log('🔄 Redux loginUser.rejected:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.userInfo = null;
        state.userToken = null;
        state.accessTokenExpiresAt = null;
        state.refreshTokenExpiresAt = null;
      })

      // Logout cases
      .addCase(logoutUser.pending, state => {
        console.log('🔄 Redux logoutUser.pending');
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, state => {
        console.log('🔄 Redux logoutUser.fulfilled');
        state.loading = false;
        state.isAuthenticated = false;
        state.userInfo = null;
        state.userToken = null;
        state.accessTokenExpiresAt = null;
        state.refreshTokenExpiresAt = null;
        state.error = null;
        state.success = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        console.log('🔄 Redux logoutUser.rejected:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })

      // Check auth status cases
      .addCase(checkAuthStatus.pending, state => {
        // console.log('🔄 Redux checkAuthStatus.pending');
        state.loading = true;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        // console.log('🔄 Redux checkAuthStatus.fulfilled:', action.payload);
        state.loading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.userInfo = action.payload.user;
          state.userToken = action.payload.token;
          state.accessTokenExpiresAt =
            (action.payload as any).accessTokenExpiresAt ?? null;
          state.refreshTokenExpiresAt =
            (action.payload as any).refreshTokenExpiresAt ?? null;
        } else {
          state.isAuthenticated = false;
          state.userInfo = null;
          state.userToken = null;
          state.accessTokenExpiresAt = null;
          state.refreshTokenExpiresAt = null;
        }
        state.error = null;
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        console.log('🔄 Redux checkAuthStatus.rejected:', action.payload);
        state.loading = false;
        state.isAuthenticated = false;
        state.userInfo = null;
        state.userToken = null;
        state.accessTokenExpiresAt = null;
        state.refreshTokenExpiresAt = null;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess, resetAuth } = authSlice.actions;
export default authSlice.reducer;
